/**
 * Transfer modal — collects transfer reasons, explanation, preferences
 * and submits a real FHIR transfer request (SR stage change + QR + preferences).
 *
 * Includes "Generate AI Summary" that calls the ai-transfer-history-summary bot
 * to populate the transfer explanation from signed EoC history for the current
 * therapist.
 *
 * PRD: docs/prd/Tasks/PRD_TRANSFER_REQUESTS_FHIR_MAPPING.md
 */

import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Group,
  Loader,
  Modal,
  MultiSelect,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notify } from '@/lib/notify';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { formatPractitionerPreferredLabel } from '@unify/fhir-model/resources/practitioner';
import { useGeminiAiOptOut } from '../../../hooks/useGeminiAiOptOut';
import {
  TRANSFER_REASON_OPTIONS,
  SESSION_FORMAT_OPTIONS,
  type TransferReasonCode,
} from '../../../lib/transferFhirConstants';
import {
  submitTransferRequest,
  type SubmitTransferRequestInput,
} from '../../../lib/transferService';
import {
  transferFormSchema,
  type TransferFormInput,
} from '../../../schemas/transfer.schema';
import type { SessionFormatPreference } from '@unify/fhir-model/resources/service-request';
import { useGenerateTransferSummary } from '../hooks/useGenerateTransferSummary';
import type { TransferSummaryBotOutput } from '../../../lib/aiTransferHistorySummaryService';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface TransferEpisodeOption {
  value: string;
  label: string;
  currentProviderDisplay?: string;
  serviceTypeDisplay?: string;
}

export interface TransferModalContext {
  patientId: string;
  patientDisplay?: string;
  episodeOfCareId: string;
  episodeDisplay?: string;
  serviceRequestId: string;
  currentProviderDisplay?: string;
  currentPractitionerId?: string;
  serviceTypeDisplay?: string;
  episodeOptions: TransferEpisodeOption[];
}

export interface TransferModalProps {
  opened: boolean;
  onClose: () => void;
  /** FHIR context for the transfer — required for live submission. */
  context: TransferModalContext | null;
  /** Called after successful FHIR submission so parent can refetch. */
  onSuccess?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const INITIAL_VALUES: TransferFormInput = {
  transferReasons: [],
  transferExplanation: '',
  recommendedClinicians: '',
  informedClient: false as unknown as true,
  sessionFormatPreference: undefined,
  availabilityPreference: [],
  areasOfFocus: [],
  providerPreferences: [],
};

export function TransferModal({
  opened,
  onClose,
  context,
  onSuccess,
}: TransferModalProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [submitting, setSubmitting] = useState(false);
  const [selectedEpisodeId, setSelectedEpisodeId] = useState<string | null>(null);
  const [aiMetadata, setAiMetadata] = useState<TransferSummaryBotOutput | null>(null);
  const [insufficientDataMessage, setInsufficientDataMessage] = useState<string | null>(null);
  const pendingReplaceRef = useRef(false);

  const form = useForm<TransferFormInput>({
    initialValues: INITIAL_VALUES,
    validate: zod4Resolver(transferFormSchema),
  });

  const practitionerId = context?.currentPractitionerId ?? profile?.id;
  const geminiAiDisabled = useGeminiAiOptOut(context?.patientId, practitionerId);

  const summaryParams = useMemo(
    () =>
      context?.patientId && selectedEpisodeId
        ? {
            patientId: context.patientId,
            episodeOfCareId: selectedEpisodeId,
          }
        : null,
    [context?.patientId, selectedEpisodeId],
  );

  const {
    generate,
    isGenerating,
    error: aiError,
    reset: resetAi,
  } = useGenerateTransferSummary(summaryParams);

  useEffect(() => {
    if (!opened) {
      form.reset();
      setSelectedEpisodeId(null);
      setAiMetadata(null);
      setInsufficientDataMessage(null);
      resetAi();
    } else if (context?.episodeOfCareId) {
      setSelectedEpisodeId(context.episodeOfCareId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, context?.episodeOfCareId]);

  const selectedEpisode = useMemo(
    () => context?.episodeOptions.find((option) => option.value === selectedEpisodeId),
    [context?.episodeOptions, selectedEpisodeId],
  );

  const handleClose = useCallback(() => {
    form.reset();
    setAiMetadata(null);
    setInsufficientDataMessage(null);
    resetAi();
    onClose();
  }, [onClose, form, resetAi]);

  const applySummary = useCallback(
    (output: TransferSummaryBotOutput) => {
      const currentText = form.values.transferExplanation.trim();
      if (currentText) {
        form.setFieldValue(
          'transferExplanation',
          `${currentText}\n\n--- AI-Generated Summary ---\n${output.summary}`,
        );
      } else {
        form.setFieldValue('transferExplanation', output.summary);
      }
      setAiMetadata(output);
      setInsufficientDataMessage(null);
    },
    [form],
  );

  const handleGenerateAiSummary = useCallback(async () => {
    if (isGenerating || !summaryParams || geminiAiDisabled) return;
    setInsufficientDataMessage(null);

    const currentText = form.values.transferExplanation.trim();
    if (currentText && !pendingReplaceRef.current) {
      pendingReplaceRef.current = true;
      notify({
        title: 'Existing text detected',
        message:
          'The AI summary will be appended below your current text. Click Generate again to confirm.',
        type: 'warning',
        autoClose: 4000,
      });
      return;
    }
    pendingReplaceRef.current = false;

    const output = await generate();
    if (!output) return;

    if (output.insufficientData) {
      setInsufficientDataMessage(
        'There is not enough signed history with this therapist for an AI summary yet. Please write the transfer summary manually.',
      );
      return;
    }

    applySummary(output);
    notify({
      title: 'AI summary generated',
      message: 'Review and edit the summary before submitting.',
      type: 'info',
      autoClose: 4000,
    });
  }, [isGenerating, summaryParams, geminiAiDisabled, form.values.transferExplanation, generate, applySummary]);

  const handleSubmit = useCallback(
    async (values: TransferFormInput) => {
      if (!context || !profile || !selectedEpisodeId) return;

      setSubmitting(true);
      try {
        const input: SubmitTransferRequestInput = {
          serviceRequestId: context.serviceRequestId,
          episodeOfCareId: selectedEpisodeId,
          patient: {
            id: context.patientId,
            display: context.patientDisplay,
          },
          author: {
            id: profile.id!,
            display: profile.resourceType === 'Practitioner'
              ? formatPractitionerPreferredLabel(profile) || undefined
              : undefined,
          },
          transferReasons: values.transferReasons as TransferReasonCode[],
          transferExplanation: values.transferExplanation,
          recommendedClinicians: values.recommendedClinicians || undefined,
          sessionFormatPreference: values.sessionFormatPreference as
            | SessionFormatPreference
            | undefined,
          availabilityPreference:
            values.availabilityPreference?.length
              ? values.availabilityPreference
              : undefined,
          areasOfFocus:
            values.areasOfFocus?.length ? values.areasOfFocus : undefined,
          providerPreferences:
            values.providerPreferences?.length
              ? values.providerPreferences
              : undefined,
          aiSummaryMetadata: aiMetadata
            ? {
                model: aiMetadata.model,
                generatedAt: aiMetadata.generatedAt,
                sourceCounts: aiMetadata.sourceCounts,
              }
            : undefined,
        };

        await submitTransferRequest(medplum, input);

        notify({
          title: 'Transfer submitted',
          message: `Transfer request created for ${context.patientDisplay ?? 'client'}.`,
          type: 'success',
        });

        handleClose();
        onSuccess?.();
      } catch (err) {
        notify({
          title: 'Transfer failed',
          error: err,
          message: 'An unexpected error occurred',
          type: 'error',
        });
      } finally {
        setSubmitting(false);
      }
    },
    [context, profile, medplum, handleClose, onSuccess, selectedEpisodeId, aiMetadata],
  );

  const canSubmit =
    !!selectedEpisodeId &&
    form.values.informedClient &&
    form.values.transferReasons.length > 0 &&
    form.values.transferExplanation.trim().length > 0;

  const canGenerateAi = !!summaryParams && !submitting && !geminiAiDisabled;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Transfer client"
      size="lg"
      centered
      closeOnClickOutside={!submitting && !isGenerating}
      closeOnEscape={!submitting && !isGenerating}
    >
      {context && (
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <Group justify="space-between" wrap="nowrap">
              <div>
                <Text fw={600}>
                  Client: {context.patientDisplay ?? 'Client'}
                </Text>
                <Text c="dimmed" size="sm">
                  Current provider:{' '}
                  {selectedEpisode?.currentProviderDisplay ??
                    context.currentProviderDisplay ??
                    '—'}
                </Text>
              </div>
            </Group>

            <Select
              label="Episode of care"
              placeholder="Select episode..."
              data={context.episodeOptions.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              value={selectedEpisodeId}
              onChange={(value) => {
                setSelectedEpisodeId(value);
                setAiMetadata(null);
                setInsufficientDataMessage(null);
                resetAi();
              }}
              withAsterisk
              required
            />

            <MultiSelect
              label="Transfer reasons"
              description="Select all that apply"
              placeholder="Select reasons..."
              data={TRANSFER_REASON_OPTIONS}
              withAsterisk
              required
              {...form.getInputProps('transferReasons')}
            />

            <div>
              <Textarea
                label="Summarized client-therapist history and transfer reasoning"
                placeholder="Enter client-therapist history and reason for transfer..."
                minRows={8}
                autosize
                required
                {...form.getInputProps('transferExplanation')}
              />
              <Group justify="space-between" mt={4}>
                <div>
                  {aiMetadata && (
                    <Text size="xs" c="dimmed" fs="italic">
                      AI-generated draft. Review and edit before submitting.
                    </Text>
                  )}
                </div>
                {!geminiAiDisabled && (
                  <Button
                    variant="light"
                    size="xs"
                    onClick={handleGenerateAiSummary}
                    disabled={!canGenerateAi || isGenerating}
                    leftSection={isGenerating ? <Loader size={12} /> : undefined}
                  >
                    {isGenerating ? 'Generating...' : 'Generate AI Summary'}
                  </Button>
                )}
              </Group>
            </div>

            {insufficientDataMessage && (
              <Alert color="yellow" variant="light">
                {insufficientDataMessage}
              </Alert>
            )}

            {aiError && (
              <Alert color="red" variant="light">
                AI summary generation failed: {aiError}
              </Alert>
            )}

            <TextInput
              label="Recommended clinicians"
              placeholder="Optional: preferred provider(s)"
              {...form.getInputProps('recommendedClinicians')}
            />

            <Divider label="Matching preferences" labelPosition="left" />

            <Select
              label="Preferred session format"
              placeholder="Select format..."
              data={SESSION_FORMAT_OPTIONS}
              clearable
              {...form.getInputProps('sessionFormatPreference')}
            />

            <MultiSelect
              label="Areas of focus"
              description="Up to 3"
              placeholder="Select areas..."
              data={[
                'Anxiety',
                'Depression',
                'Trauma / PTSD',
                'Relationship Issues',
                'Grief / Loss',
                'Life Transitions',
                'Self-Esteem',
                'Stress Management',
                'Anger Management',
                'OCD',
                'ADHD',
                'Substance Use',
                'Eating Disorders',
              ]}
              maxValues={3}
              clearable
              {...form.getInputProps('areasOfFocus')}
            />

            <MultiSelect
              label="Provider preferences"
              placeholder="Select preferences..."
              data={[
                'No preference',
                'Same gender',
                'BIPOC provider',
                'LGBTQIA+ affirming',
                'Faith-based',
                'Bilingual / Spanish',
              ]}
              clearable
              {...form.getInputProps('providerPreferences')}
            />

            <Checkbox
              label="I have informed the client that I have submitted a transfer request."
              {...form.getInputProps('informedClient', { type: 'checkbox' })}
            />

            <Group justify="flex-end" mt="md">
              <Button
                variant="default"
                onClick={handleClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit || submitting}
                leftSection={submitting ? <Loader size={14} /> : undefined}
              >
                {submitting ? 'Submitting...' : 'Submit Transfer Request'}
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
